import {
  fetchProfile,
  isAccountsConfigured,
  loginWithPassword,
  logout as accountsLogout,
} from '../helpers/accountsAuth';
import { apiCall } from '../helpers/apiHelpers';
import { ApiResult } from '../types/api/common';
import { LoginResponse } from '../types/api/responses';
import { User } from '../types/models/user';

const url = {
  login: '/client/login',
  logout: '/client/logout?clientSessionId=',
};

export const login = apiCall<LoginResponse>({
  method: 'POST',
  url: url.login,
});

export const logout = (sessionId: string): Promise<ApiResult<void>> =>
  apiCall({
    method: 'GET',
    url: `${url.logout}${sessionId}`,
  })();

export const getUserInfoById = (
  sessionId: string,
): Promise<ApiResult<LoginResponse>> =>
  apiCall<LoginResponse>({
    method: 'GET',
    url: `/client/session?clientSessionId=${sessionId}`,
  })();

/**
 * DEV-101: вход через osago-accounts, со старым путём как запасным.
 *
 * Неверный пароль запасным путём НЕ перепроверяем: пароль один и тот же в обеих
 * системах, второй заход только удвоит попытки и приблизит блокировку. Фолбэк
 * нужен ровно для одного случая: сервис авторизации недоступен.
 */
async function loginViaAccounts(
  email: string,
  password: string,
): Promise<{ error: string | null; user: User | null; unavailable?: boolean }> {
  let error: string | null;
  try {
    error = await loginWithPassword(email, password);
  } catch {
    return { error: null, user: null, unavailable: true };
  }
  if (error) return { error, user: null };

  const profile = await fetchProfile();
  return {
    error: null,
    user: {
      // Старой сессии тут нет: запросы уходят с Bearer. Поле оставлено пустым,
      // потому что сервисы ЛК по-прежнему подставляют его в query.
      sessionId: '',
      name: profile?.fullName || '',
      email: profile?.email || email,
    },
  };
}

async function loginViaLegacy(
  email: string,
  password: string,
): Promise<{ error: string | null; user: User | null }> {
  const [err, res] = await login({ email, password });

  if (err || res?.error) return { error: res?.message || 'Ошибка входа', user: null };
  if (!res?.data) {
    return {
      error:
        'На сайте ведутся технические работы! Пожалуйста, обновите страничку спустя одну минуту.',
      user: null,
    };
  }

  const { sessionId, client } = res.data;
  return {
    error: null,
    user: { sessionId, name: client.fullName, email: client.email },
  };
}

export async function loginUser(
  email: string,
  password: string,
): Promise<{ error: string | null; user: User | null }> {
  if (isAccountsConfigured()) {
    const result = await loginViaAccounts(email, password);
    if (!result.unavailable) return { error: result.error, user: result.user };
    // сервис авторизации не ответил — пробуем прежний путь, он ещё жив
  }
  return loginViaLegacy(email, password);
}

/** Выход гасим с обеих сторон: у человека может быть и токен, и старая сессия. */
export async function logoutUser(sessionId: string): Promise<void> {
  if (isAccountsConfigured()) await accountsLogout();
  if (sessionId) await logout(sessionId);
}
